import { TreeNode, NodeType } from '@/types/tree';
import { SampleActivityDto, ProjectActivityDto, SampleDto, TheoryActivityDto, DataPackageDto } from '@/api/ApiClient';

/**
 * Sort direction for samples
 */
export enum SampleSortDirection {
  OLD_TO_NEW = 'old-to-new',
  NEW_TO_OLD = 'new-to-old',
}

/**
 * Utility class for sorting tree nodes according to specific business rules.
 */
export class TreeSorting {
  /**
   * Sorts tree nodes according to the defined business rules:
   * 1. Sort by type first (project children, dataPackage children, etc.)
   * 2. For activities and samples, sort by date
   * 3. Within the same date, apply specific ordering rules
   * @param nodes - Array of tree nodes to sort
   * @param sampleSortDirection - Direction to sort samples (default: OLD_TO_NEW)
   */
  static sort(
    nodes: TreeNode[],
    getData: (dataId: string) => any,
    sampleSortDirection: SampleSortDirection = SampleSortDirection.OLD_TO_NEW
  ): TreeNode[] {
    return nodes.sort((a, b) => {
      // Rule 1: Sort by type first
      const typeOrder = this.getTypeOrder(a.type, b.type);
      if (typeOrder !== 0) {
        return typeOrder;
      }

      // Rule 2: For samples and activities, sort by date
      if (this.isDateSortable(a.type) && this.isDateSortable(b.type)) {
        const dateComparison = this.compareByDate(a, b, sampleSortDirection, getData);
        if (dateComparison !== 0) {
          return dateComparison;
        }

        // Rule 3: Within the same date, apply specific ordering rules
        return this.compareWithinSameDate(a, b, sampleSortDirection, getData);
      }

      // For non-date sortable types, maintain current order
      return 0;
    });
  }

  /**
   * Determines the order of node types based on business rules.
   */
  private static getTypeOrder(typeA: NodeType, typeB: NodeType): number {
    // Define type order based on context
    const getOrder = (type: NodeType): number => {
      // Project children order
      if (type === 'projectActivity') return 1;
      if (type === 'communications') return 2;
      if (type === 'shippings') return 3;
      if (type === 'dataPackage') return 4;
      if (type === 'oldSamples') return 5;
      if (type === 'newSamples') return 6;
      if (type === 'simulations') return 7;

      // DataPackage children order
      if (type === 'theoryActivity') return 1;
      if (type === 'sample') return 2;

      // Sample group nodes (same type order - date sorting handles chronological order)
      if (type === 'sampleYearGroup') return 3;
      if (type === 'sampleQuarterGroup') return 3; // Same as year groups

      // Other types (maintain current order)
      return 0;
    };

    const orderA = getOrder(typeA);
    const orderB = getOrder(typeB);

    return orderA - orderB;
  }

  /**
   * Checks if a node type should be sorted by date.
   */
  private static isDateSortable(type: NodeType): boolean {
    return (
      type === 'sampleActivity' ||
      type === 'sampleActivityAggregate' ||
      type === 'sampleActivitySnapshot' ||
      type === 'sampleActivityInPackage' ||
      type === 'projectActivity' ||
      type === 'theoryActivity' ||
      type === 'sample' ||
      type === 'sampleAggregate' ||
      type === 'sampleSnapshot' ||
      type === 'sampleInPackage' ||
      type === 'simulationDataset' ||
      type === 'sampleYearGroup' ||
      type === 'sampleQuarterGroup'
    );
  }

  /**
   * Compares two nodes by their date.
   */
  private static compareByDate(
    a: TreeNode,
    b: TreeNode,
    sampleSortDirection: SampleSortDirection,
    getData: (dataId: string) => any
  ): number {
    const dateA = this.getNodeDate(a, getData);
    const dateB = this.getNodeDate(b, getData);

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();

    // For samples and sample groups, apply the sort direction
    const isSampleType = (type: NodeType) =>
      type === 'sample' ||
      type === 'sampleAggregate' ||
      type === 'sampleSnapshot' ||
      type === 'sampleInPackage' ||
      type === 'sampleYearGroup' ||
      type === 'sampleQuarterGroup';
    if (isSampleType(a.type) && isSampleType(b.type)) {
      return sampleSortDirection === SampleSortDirection.OLD_TO_NEW ? timeA - timeB : timeB - timeA;
    }

    // For activities and simulation datasets, always sort old to new
    return timeA - timeB;
  }

  /**
   * Extracts the date from a node based on its type.
   */
  private static getNodeDate(node: TreeNode, getData: (dataId: string) => any): Date | undefined {
    const nodeData = getData(node.dataId);
    if (
      node.type === 'sampleActivity' ||
      node.type === 'sampleActivityAggregate' ||
      node.type === 'sampleActivitySnapshot' ||
      node.type === 'sampleActivityInPackage'
    ) {
      return (nodeData as SampleActivityDto)?.date;
    } else if (node.type === 'projectActivity') {
      return (nodeData as ProjectActivityDto)?.date;
    } else if (node.type === 'theoryActivity') {
      return (nodeData as TheoryActivityDto)?.date;
    } else if (
      node.type === 'sample' ||
      node.type === 'sampleAggregate' ||
      node.type === 'sampleSnapshot' ||
      node.type === 'sampleInPackage'
    ) {
      return (nodeData as SampleDto)?.creationDate;
    } else if (node.type === 'simulationDataset') {
      return (nodeData as DataPackageDto)?.lastChangedDate;
    } else if (node.type === 'sampleYearGroup') {
      // Use January 1st of the group year for sorting
      const groupData = nodeData as any;
      return new Date(groupData.year, 0, 1);
    } else if (node.type === 'sampleQuarterGroup') {
      // Use first day of the quarter for sorting
      const groupData = nodeData as any;
      // Updated to parse "2023 Q1" format
      const quarterMatch = groupData.quarter?.match(/(\d+) Q(\d+)/);
      if (quarterMatch) {
        const year = parseInt(quarterMatch[1]);
        const quarter = parseInt(quarterMatch[2]);
        const month = (quarter - 1) * 3; // Q1=0, Q2=3, Q3=6, Q4=9
        return new Date(year, month, 1);
      }
    }
    return undefined;
  }

  /**
   * Compares nodes within the same date according to specific business rules.
   */
  private static compareWithinSameDate(
    a: TreeNode,
    b: TreeNode,
    sampleSortDirection: SampleSortDirection,
    getData: (dataId: string) => any
  ): number {
    // For Sample Groups: Quarters before year groups when from the same year
    if (
      (a.type === 'sampleYearGroup' || a.type === 'sampleQuarterGroup') &&
      (b.type === 'sampleYearGroup' || b.type === 'sampleQuarterGroup')
    ) {
      const aData = getData(a.dataId) as any;
      const bData = getData(b.dataId) as any;

      // Only apply quarter-before-year logic if they're from the same year
      if (aData?.year === bData?.year) {
        if (a.type === 'sampleQuarterGroup' && b.type === 'sampleYearGroup') {
          return -1; // Quarter before year (same year)
        }
        if (a.type === 'sampleYearGroup' && b.type === 'sampleQuarterGroup') {
          return 1; // Quarter before year (same year)
        }
      }
    }

    // For Project Activities: Proposal first
    if (a.type === 'projectActivity' && b.type === 'projectActivity') {
      const aData = getData(a.dataId) as ProjectActivityDto;
      const bData = getData(b.dataId) as ProjectActivityDto;

      // Handle null data gracefully
      if (!aData && !bData) return 0;
      if (!aData) return 1;
      if (!bData) return -1;

      const aIsProposal = aData.type?.toLowerCase() === 'proposal';
      const bIsProposal = bData.type?.toLowerCase() === 'proposal';

      if (aIsProposal && !bIsProposal) return -1;
      if (!aIsProposal && bIsProposal) return 1;
    }

    // For Sample Activities: Apply category and type ordering
    if (
      (a.type === 'sampleActivity' ||
        a.type === 'sampleActivityAggregate' ||
        a.type === 'sampleActivitySnapshot' ||
        a.type === 'sampleActivityInPackage') &&
      (b.type === 'sampleActivity' ||
        b.type === 'sampleActivityAggregate' ||
        b.type === 'sampleActivitySnapshot' ||
        b.type === 'sampleActivityInPackage')
    ) {
      const aData = getData(a.dataId) as SampleActivityDto;
      const bData = getData(b.dataId) as SampleActivityDto;

      // Handle null data gracefully
      if (!aData && !bData) return 0;
      if (!aData) return 1;
      if (!bData) return -1;

      return this.compareSampleActivities(aData, bData);
    }

    // For Samples: Sort by sampleLabel
    if (
      (a.type === 'sample' ||
        a.type === 'sampleAggregate' ||
        a.type === 'sampleSnapshot' ||
        a.type === 'sampleInPackage') &&
      (b.type === 'sample' ||
        b.type === 'sampleAggregate' ||
        b.type === 'sampleSnapshot' ||
        b.type === 'sampleInPackage')
    ) {
      const aData = getData(a.dataId) as SampleDto;
      const bData = getData(b.dataId) as SampleDto;

      // Handle null data gracefully
      if (!aData && !bData) return 0;
      if (!aData) return 1;
      if (!bData) return -1;

      const labelComparison = (aData.sampleLabel || '').localeCompare(bData.sampleLabel || '');

      return sampleSortDirection === SampleSortDirection.OLD_TO_NEW ? labelComparison : -labelComparison;
    }

    return 0;
  }

  /**
   * Compares two sample activities according to category and type ordering rules.
   */
  private static compareSampleActivities(a: SampleActivityDto, b: SampleActivityDto): number {
    const categoryOrder = this.getSampleActivityCategoryOrder(a.category, a.type, a.technique, a.instrument);
    const categoryOrderB = this.getSampleActivityCategoryOrder(b.category, b.type, b.technique, b.instrument);

    return categoryOrder - categoryOrderB;
  }

  /**
   * Determines the order of sample activities based on category, type, technique, and instrument.
   * Order: INVE (1) -> PROC PREP without technique/instrument (2) -> PROC PREP with technique/instrument (3)
   * -> PROC SYN without technique/instrument (4) -> PROC POST without technique/instrument (5)
   * -> CHAR (6) -> STOR (7) -> PROC DIC (8)
   */
  private static getSampleActivityCategoryOrder(
    category?: string,
    type?: string,
    technique?: string,
    instrument?: string
  ): number {
    if (!category) return 999;

    switch (category.toUpperCase()) {
      case 'INVE':
        return 1;
      case 'PROC':
        if (type?.toUpperCase() === 'PREP') {
          if (!technique && !instrument) return 2;
          if (technique || instrument) return 3;
        } else if (type?.toUpperCase() === 'SYN') {
          if (!technique && !instrument) return 4;
        } else if (type?.toUpperCase() === 'POST') {
          if (!technique && !instrument) return 5;
        } else if (type?.toUpperCase() === 'DIC') {
          return 8;
        }
        return 6; // Other PROC types
      case 'CHAR':
        return 6;
      case 'STOR':
        return 7;
      default:
        return 999;
    }
  }
}
